import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, ChevronLeft, AlertCircle } from 'lucide-react';
import { MainLayout } from '../../components/layout/MainLayout';
import {
  salesByVehicleService,
  VehicleSalesRow,
  MaterialTrimRow,
  SalesDiagnostics,
  SalesByVehicleResult,
} from '../../api/services/salesByVehicleService';

// ─── Helpers ────────────────────────────────────────────────────────────────

function todayStr() { return new Date().toISOString().slice(0, 10); }
function firstOfYearStr() { return `${new Date().getFullYear()}-01-01`; }
function fmt(n: number) { return n.toLocaleString(); }
function fmtRevenue(n: number) {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toLocaleString()}`;
}

const IMPACT: React.CSSProperties = { fontFamily: "Impact, 'Arial Narrow', 'Arial Black', sans-serif" };
const MONO: React.CSSProperties   = { fontFamily: "Consolas, 'Courier New', monospace" };

// ─── Data helpers ────────────────────────────────────────────────────────────

interface MaterialGroup {
  material: string;
  unitsSold: number;
  orderCount: number;
  revenue: number;
  trims: { trimCode: string; unitsSold: number; orderCount: number; revenue: number }[];
}

function groupByMaterial(rows: MaterialTrimRow[]): MaterialGroup[] {
  const map = new Map<string, MaterialGroup>();
  for (const r of rows) {
    const key = r.Material || '(none)';
    if (!map.has(key)) map.set(key, { material: key, unitsSold: 0, orderCount: 0, revenue: 0, trims: [] });
    const g = map.get(key)!;
    g.unitsSold  += r.UnitsSold;
    g.orderCount += r.OrderCount;
    g.revenue    += r.Revenue ?? 0;
    g.trims.push({ trimCode: r.TrimCode || '(base)', unitsSold: r.UnitsSold, orderCount: r.OrderCount, revenue: r.Revenue ?? 0 });
  }
  return Array.from(map.values()).sort((a, b) => b.unitsSold - a.unitsSold);
}

// ─── Section title ────────────────────────────────────────────────────────────

const SectionTitle: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex items-center gap-3 my-2">
    <span style={IMPACT} className="text-sm tracking-widest text-gray-400 dark:text-gray-500 uppercase whitespace-nowrap">
      {label}
    </span>
    <div className="flex-1 h-px bg-gray-200 dark:bg-[#2a3140]" />
  </div>
);

// ─── Level 1 — Vehicle card ───────────────────────────────────────────────────

const VehicleCard: React.FC<{
  row: VehicleSalesRow; rank: number; maxUnits: number; onClick: () => void;
}> = ({ row, rank, maxUnits, onClick }) => {
  const pct = maxUnits > 0 ? (row.UnitsSold / maxUnits) * 100 : 0;

  return (
    <button
      onClick={onClick}
      className="relative w-full text-left bg-white dark:bg-[#161a20] border border-gray-200 dark:border-[#2a3140]
                 rounded-xl p-5 overflow-hidden
                 hover:border-amber-400 dark:hover:border-amber-500
                 hover:-translate-y-0.5 hover:shadow-lg dark:hover:shadow-amber-900/10
                 transition-all duration-200"
    >
      {/* Rank badge — top-right corner */}
      <div
        style={IMPACT}
        className={`absolute top-0 right-0 px-3 py-0.5 rounded-bl-xl text-sm
          ${rank === 1
            ? 'bg-amber-500 text-black'
            : 'bg-gray-100 dark:bg-[#1e242d] border-l border-b border-gray-200 dark:border-[#2a3140] text-gray-400 dark:text-gray-500 text-xs'
          }`}
      >
        #{rank}
      </div>

      {/* Make */}
      <p className="text-[10px] font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-widest mb-1">
        {row.Make}
      </p>

      {/* Model */}
      <p className="text-xl text-gray-900 dark:text-white leading-tight pr-10 mb-3"
         style={{ ...IMPACT, letterSpacing: '1.5px' }}>
        {row.Model}
      </p>

      {/* Units */}
      <p className="text-2xl leading-none text-amber-500 dark:text-amber-400" style={MONO}>
        {fmt(row.UnitsSold)}
      </p>
      <p className="text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-widest mt-0.5">units sold</p>

      {/* Revenue */}
      <p className="text-sm font-semibold text-green-600 dark:text-green-400 mt-1.5" style={MONO}>
        {fmtRevenue(row.Revenue ?? 0)}
      </p>

      {/* Gradient bar */}
      <div className="mt-3 h-1 bg-gray-100 dark:bg-[#1e242d] rounded-full overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{ width: `${pct}%`, background: 'linear-gradient(90deg,#3b82f6,#f4a520)', transition: 'width 0.6s ease' }}
        />
      </div>
    </button>
  );
};

// ─── Level 2 — Fabric card ────────────────────────────────────────────────────

const MaterialCard: React.FC<{
  group: MaterialGroup; maxUnits: number; totalUnits: number; onClick: () => void;
}> = ({ group, maxUnits, totalUnits, onClick }) => {
  const pct      = maxUnits > 0 ? (group.unitsSold / maxUnits) * 100 : 0;
  const sharePct = totalUnits > 0 ? ((group.unitsSold / totalUnits) * 100).toFixed(1) : '0.0';
  const topTrims = group.trims.slice(0, 3);

  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-white dark:bg-[#161a20] border border-gray-200 dark:border-[#2a3140]
                 rounded-xl p-5
                 hover:border-green-400 dark:hover:border-green-500
                 hover:-translate-y-0.5 hover:shadow-lg dark:hover:shadow-green-900/10
                 transition-all duration-200"
    >
      {/* Name */}
      <p className="text-sm font-semibold text-gray-900 dark:text-white mb-0.5">{group.material}</p>
      <p className="text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">
        {group.trims.length} trim{group.trims.length !== 1 ? 's' : ''}
      </p>

      {/* Units + share */}
      <div className="flex items-baseline gap-2 mb-2.5">
        <span className="text-2xl text-green-500 dark:text-green-400" style={MONO}>
          {fmt(group.unitsSold)}
        </span>
        <span className="text-xs text-gray-400 dark:text-gray-500" style={MONO}>{sharePct}%</span>
      </div>

      {/* Green gradient bar */}
      <div className="h-1 bg-gray-100 dark:bg-[#1e242d] rounded-full overflow-hidden mb-3">
        <div
          className="h-full rounded-full"
          style={{ width: `${pct}%`, background: 'linear-gradient(90deg,#22c55e,#86efac)', transition: 'width 0.6s ease' }}
        />
      </div>

      {/* Trim chips preview */}
      <div className="flex flex-wrap gap-1">
        {topTrims.map(t => (
          <span
            key={t.trimCode}
            className="text-[10px] px-2 py-0.5 rounded bg-gray-100 dark:bg-[#1e242d]
                       border border-gray-200 dark:border-[#2a3140] text-gray-500 dark:text-gray-400 font-medium"
          >
            {t.trimCode}
          </span>
        ))}
        {group.trims.length > 3 && (
          <span className="text-[10px] px-2 py-0.5 rounded bg-gray-100 dark:bg-[#1e242d]
                           border border-gray-200 dark:border-[#2a3140] text-gray-400 dark:text-gray-500">
            +{group.trims.length - 3} more
          </span>
        )}
      </div>
    </button>
  );
};

// ─── Level 3 — Trim rows ──────────────────────────────────────────────────────

const TrimView: React.FC<{
  vehicle: VehicleSalesRow; material: MaterialGroup; totalModelUnits: number; onBack: () => void;
}> = ({ vehicle, material, totalModelUnits, onBack }) => {
  const maxTrimUnits = material.trims.length > 0 ? material.trims[0].unitsSold : 1;

  return (
    <div className="space-y-5">
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-[10px] font-semibold text-gray-400 dark:text-gray-500
                   hover:text-gray-900 dark:hover:text-white transition-colors uppercase tracking-widest"
      >
        <ChevronLeft size={13} />
        Back to {vehicle.Make} {vehicle.Model}
      </button>

      {/* Color header */}
      <div className="bg-white dark:bg-[#161a20] border border-gray-200 dark:border-[#2a3140] rounded-xl p-6">
        <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-0.5">
          {vehicle.Make} {vehicle.Model} · Fabric
        </p>
        <p className="text-3xl text-green-600 dark:text-green-400 mb-4" style={{ ...IMPACT, letterSpacing: '2px' }}>
          {material.material}
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Units Sold',    value: fmt(material.unitsSold),       cls: 'text-amber-500 dark:text-amber-400' },
            { label: 'Revenue',       value: fmtRevenue(material.revenue),  cls: 'text-green-600 dark:text-green-400' },
            { label: 'Orders',        value: fmt(material.orderCount),      cls: 'text-blue-600 dark:text-blue-400' },
            { label: 'Trims',         value: String(material.trims.length), cls: 'text-gray-700 dark:text-gray-300' },
          ].map(({ label, value, cls }) => (
            <div key={label} className="bg-gray-50 dark:bg-[#1e242d] rounded-lg px-4 py-3">
              <p className="text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-1">{label}</p>
              <p className={`text-2xl ${cls}`} style={MONO}>{value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Trim list */}
      <SectionTitle label={`${material.trims.length} Trim${material.trims.length !== 1 ? 's' : ''}`} />
      <div className="bg-white dark:bg-[#161a20] border border-gray-200 dark:border-[#2a3140] rounded-xl overflow-hidden">
        <div className="divide-y divide-gray-100 dark:divide-[#2a3140]">
          {material.trims.map((trim, idx) => {
            const pct   = maxTrimUnits > 0 ? (trim.unitsSold / maxTrimUnits) * 100 : 0;
            const share = totalModelUnits > 0 ? ((trim.unitsSold / totalModelUnits) * 100).toFixed(1) : '0.0';
            return (
              <div
                key={`${trim.trimCode}-${idx}`}
                className="px-5 py-3 grid items-center gap-4
                           hover:bg-gray-50 dark:hover:bg-[#1e242d] transition-colors"
                style={{ gridTemplateColumns: '1fr auto 140px' }}
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                    {trim.trimCode}
                  </p>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500" style={MONO}>
                    {fmtRevenue(trim.revenue)} · {share}%
                  </p>
                </div>
                <span className="text-lg text-amber-500 dark:text-amber-400 text-right shrink-0" style={MONO}>
                  {fmt(trim.unitsSold)}
                </span>
                <div className="h-2 bg-gray-100 dark:bg-[#1e242d] rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${pct}%`, background: 'linear-gradient(90deg,#3b82f6,#f4a520)', transition: 'width 0.5s ease' }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ─── Level 2 — Model detail with fabric grid ──────────────────────────────────

const ModelDetail: React.FC<{
  row: VehicleSalesRow; rank: number;
  totalUnits: number; dateFrom: string; dateTo: string; onBack: () => void;
}> = ({ row, rank, totalUnits, dateFrom, dateTo, onBack }) => {
  const [selectedMaterial, setSelectedMaterial] = useState<MaterialGroup | null>(null);

  const { data: rawBreakdown = [], isLoading: loadingBreakdown } = useQuery({
    queryKey: ['sales-by-vehicle-breakdown', row.MakeId, row.ModelId, dateFrom, dateTo],
    queryFn:  () => salesByVehicleService.getMaterialBreakdown(row.MakeId, row.ModelId, dateFrom, dateTo),
  });

  const materials        = useMemo(() => groupByMaterial(rawBreakdown), [rawBreakdown]);
  const maxMaterialUnits = materials.length > 0 ? materials[0].unitsSold : 1;
  const avgPerOrder      = row.OrderCount > 0 ? (row.UnitsSold / row.OrderCount).toFixed(1) : '—';
  const sharePct         = totalUnits > 0 ? ((row.UnitsSold / totalUnits) * 100).toFixed(1) : '0.0';

  if (selectedMaterial) {
    return (
      <TrimView
        vehicle={row}
        material={selectedMaterial}
        totalModelUnits={row.UnitsSold}
        onBack={() => setSelectedMaterial(null)}
      />
    );
  }

  return (
    <div className="space-y-5">
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-[10px] font-semibold text-gray-400 dark:text-gray-500
                   hover:text-gray-900 dark:hover:text-white transition-colors uppercase tracking-widest"
      >
        <ChevronLeft size={13} />
        All Vehicles
      </button>

      {/* Drill header */}
      <div className="bg-white dark:bg-[#161a20] border border-gray-200 dark:border-[#2a3140] rounded-xl p-6 flex items-start gap-6">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-widest mb-0.5">
            {row.Make} · Rank #{rank}
          </p>
          <p className="text-[30px] text-gray-900 dark:text-white leading-none"
             style={{ ...IMPACT, letterSpacing: '2px' }}>
            {row.Model}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-[42px] leading-none text-amber-500 dark:text-amber-400" style={IMPACT}>
            {fmt(row.UnitsSold)}
          </p>
          <p className="text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-widest mt-0.5">Units Sold</p>
        </div>
      </div>

      {/* Sub-stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Revenue',       value: fmtRevenue(row.Revenue ?? 0), accent: 'bg-green-500' },
          { label: 'Orders',        value: fmt(row.OrderCount),          accent: 'bg-blue-500' },
          { label: 'Units / Order', value: avgPerOrder,                  accent: 'bg-amber-500' },
          { label: 'Market Share',  value: `${sharePct}%`,              accent: 'bg-indigo-500' },
        ].map(({ label, value, accent }) => (
          <div key={label} className="relative bg-white dark:bg-[#161a20] border border-gray-200 dark:border-[#2a3140] rounded-xl px-4 py-4 overflow-hidden">
            <div className={`absolute top-0 left-0 right-0 h-0.5 ${accent}`} />
            <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-1">{label}</p>
            <p className="text-2xl text-gray-900 dark:text-white" style={MONO}>{value}</p>
          </div>
        ))}
      </div>

      {/* Fabrics */}
      <SectionTitle label="Fabrics Sold" />

      {loadingBreakdown && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-500" />
        </div>
      )}

      {!loadingBreakdown && materials.length === 0 && (
        <div className="text-center py-10 text-sm text-gray-400 dark:text-gray-500">
          No fabric breakdown available for this vehicle.
        </div>
      )}

      {!loadingBreakdown && materials.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {materials.map(g => (
            <MaterialCard
              key={g.material}
              group={g}
              maxUnits={maxMaterialUnits}
              totalUnits={row.UnitsSold}
              onClick={() => setSelectedMaterial(g)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Page ────────────────────────────────────────────────────────────────────

type Tab = 'top20' | 'all';

const SalesByVehicle: React.FC = () => {
  const [dateFrom, setDateFrom] = useState(firstOfYearStr());
  const [dateTo,   setDateTo]   = useState(todayStr());
  const [tab,      setTab]      = useState<Tab>('top20');
  const [search,   setSearch]   = useState('');
  const [selected, setSelected] = useState<VehicleSalesRow | null>(null);

  const { data: result, isLoading, isError, refetch } = useQuery<SalesByVehicleResult>({
    queryKey: ['sales-by-vehicle', dateFrom, dateTo],
    queryFn:  () => salesByVehicleService.getSalesByVehicle(dateFrom, dateTo),
    enabled:  !!dateFrom && !!dateTo,
  });

  const data            = result?.vehicles ?? [];
  const showDiagnostics = !isLoading && !isError && data.length === 0;

  const { data: diagnostics } = useQuery<SalesDiagnostics>({
    queryKey: ['sales-by-vehicle-diag', dateFrom, dateTo],
    queryFn:  () => salesByVehicleService.getDiagnostics(dateFrom, dateTo),
    enabled:  showDiagnostics,
  });

  const sorted = useMemo(() => [...data].sort((a, b) => b.UnitsSold - a.UnitsSold), [data]);
  const rankMap = useMemo(() => {
    const m = new Map<number, number>();
    sorted.forEach((r, i) => m.set(r.ModelId, i + 1));
    return m;
  }, [sorted]);

  const top20       = useMemo(() => sorted.slice(0, 20), [sorted]);
  const filteredAll = useMemo(() => {
    if (!search.trim()) return sorted;
    const q = search.toLowerCase();
    return sorted.filter(r => r.Make.toLowerCase().includes(q) || r.Model.toLowerCase().includes(q));
  }, [sorted, search]);

  const displayed    = tab === 'top20' ? top20 : filteredAll;
  const maxUnits     = sorted.length > 0 ? sorted[0].UnitsSold : 1;
  const totalUnits   = result?.totalUnitsSold ?? 0;
  const totalRevenue = result?.totalRevenue   ?? 0;
  const makeCount    = useMemo(() => new Set(data.map(r => r.MakeId)).size, [data]);

  useEffect(() => { setSelected(null); }, [dateFrom, dateTo]);

  const yearLabel = dateFrom.slice(0, 4) !== dateTo.slice(0, 4)
    ? `${dateFrom.slice(0, 4)}–${dateTo.slice(0, 4)}`
    : dateFrom.slice(0, 4);

  return (
    <MainLayout>
      <div className="max-w-6xl mx-auto space-y-5">

        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl text-gray-900 dark:text-white" style={IMPACT}>
              Sales By Vehicle
            </h1>
            <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 uppercase tracking-widest">
              Units sold ranked by vehicle model
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-[10px] text-gray-400 dark:text-gray-500 font-semibold uppercase tracking-widest">From</label>
            <input
              type="date"
              value={dateFrom}
              max={dateTo}
              onChange={e => setDateFrom(e.target.value)}
              className="text-sm border border-gray-300 dark:border-[#2a3140] rounded-lg px-3 py-1.5
                         bg-white dark:bg-[#161a20] text-gray-900 dark:text-white
                         focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
            <label className="text-[10px] text-gray-400 dark:text-gray-500 font-semibold uppercase tracking-widest">To</label>
            <input
              type="date"
              value={dateTo}
              min={dateFrom}
              max={todayStr()}
              onChange={e => setDateTo(e.target.value)}
              className="text-sm border border-gray-300 dark:border-[#2a3140] rounded-lg px-3 py-1.5
                         bg-white dark:bg-[#161a20] text-gray-900 dark:text-white
                         focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center py-28">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
          </div>
        )}

        {/* Error */}
        {isError && (
          <div className="bg-white dark:bg-[#161a20] rounded-xl border border-gray-200 dark:border-[#2a3140]
                          flex flex-col items-center justify-center py-24 gap-4">
            <p className="text-red-500 text-sm font-medium">Failed to load sales data.</p>
            <button onClick={() => refetch()} className="text-sm text-blue-600 dark:text-blue-400 underline">Retry</button>
          </div>
        )}

        {/* Diagnostics */}
        {showDiagnostics && (
          <div className="bg-white dark:bg-[#161a20] rounded-xl border border-gray-200 dark:border-[#2a3140] p-6 space-y-4">
            <div className="flex items-start gap-3">
              <AlertCircle size={20} className="text-amber-500 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-gray-900 dark:text-white text-sm">No vehicle sales data found</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Orders exist but no part numbers matched the vehicle applications catalog.
                </p>
              </div>
            </div>
            {diagnostics && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 border-t border-gray-100 dark:border-[#2a3140] pt-4">
                {[
                  { label: 'Orders in range',      value: diagnostics.OrderCount },
                  { label: 'Order items in range',  value: diagnostics.ItemCount },
                  { label: 'Part catalog entries',  value: diagnostics.PartAppCount },
                  { label: 'Matched parts',         value: diagnostics.ExactMatchCount },
                ].map(({ label, value }) => (
                  <div key={label} className="bg-gray-50 dark:bg-[#1e242d] rounded-lg px-4 py-3">
                    <p className="text-xs text-gray-400 dark:text-gray-500">{label}</p>
                    <p className={`text-lg font-bold mt-0.5 ${value === 0 ? 'text-red-500' : 'text-gray-900 dark:text-white'}`}>
                      {value.toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Main content */}
        {!isLoading && !isError && data.length > 0 && (
          selected ? (
            <ModelDetail
              row={selected}
              rank={rankMap.get(selected.ModelId) ?? 0}
              totalUnits={totalUnits}
              dateFrom={dateFrom}
              dateTo={dateTo}
              onBack={() => setSelected(null)}
            />
          ) : (
            <div className="space-y-4">

              {/* Stats row — colored top-border tiles */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Total Units Sold', value: fmt(totalUnits),          accent: 'bg-amber-500' },
                  { label: 'Total Revenue',    value: fmtRevenue(totalRevenue), accent: 'bg-green-500' },
                  { label: 'Vehicle Models',   value: fmt(data.length),         accent: 'bg-blue-500' },
                  { label: 'Makes',            value: fmt(makeCount),           accent: 'bg-indigo-500' },
                ].map(({ label, value, accent }) => (
                  <div key={label} className="relative bg-white dark:bg-[#161a20] border border-gray-200 dark:border-[#2a3140] rounded-xl px-5 py-4 overflow-hidden">
                    <div className={`absolute top-0 left-0 right-0 h-0.5 ${accent}`} />
                    <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-1">{label}</p>
                    <p className="text-3xl text-gray-900 dark:text-white" style={IMPACT}>{value}</p>
                  </div>
                ))}
              </div>

              {/* Tab bar */}
              <div className="flex items-center gap-1 bg-gray-100 dark:bg-[#161a20] rounded-xl p-1
                              border border-gray-200 dark:border-[#2a3140] w-fit">
                <button
                  onClick={() => setTab('top20')}
                  className={`py-1.5 px-5 rounded-lg text-xs font-semibold transition-all duration-150 ${
                    tab === 'top20'
                      ? 'bg-amber-500 text-black'
                      : 'text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  Top 20
                </button>
                <button
                  onClick={() => { setTab('all'); setSearch(''); }}
                  className={`py-1.5 px-5 rounded-lg text-xs font-semibold transition-all duration-150 ${
                    tab === 'all'
                      ? 'bg-blue-500 text-white'
                      : 'text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  All {fmt(data.length)} Vehicles
                </button>
              </div>

              <SectionTitle
                label={
                  tab === 'top20'
                    ? `Top 20 — ${yearLabel}`
                    : search
                      ? `${filteredAll.length} result${filteredAll.length !== 1 ? 's' : ''} — ${yearLabel}`
                      : `All Vehicles — ${yearLabel}`
                }
              />

              {/* Search (All tab only) */}
              {tab === 'all' && (
                <div className="relative">
                  <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Search make or model…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="w-full pl-10 pr-14 py-2.5 text-sm border border-gray-300 dark:border-[#2a3140] rounded-xl
                               bg-white dark:bg-[#161a20] text-gray-900 dark:text-white placeholder-gray-400
                               focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                  {search && (
                    <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 dark:text-gray-500" style={MONO}>
                      {filteredAll.length}
                    </span>
                  )}
                </div>
              )}

              {/* Vehicle grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {displayed.map(row => (
                  <VehicleCard
                    key={`${row.MakeId}-${row.ModelId}`}
                    row={row}
                    rank={rankMap.get(row.ModelId) ?? 0}
                    maxUnits={maxUnits}
                    onClick={() => setSelected(row)}
                  />
                ))}
              </div>

              {tab === 'all' && filteredAll.length === 0 && (
                <div className="text-center py-12 text-sm text-gray-400 dark:text-gray-500">
                  No vehicles match &ldquo;{search}&rdquo;
                </div>
              )}

            </div>
          )
        )}

      </div>
    </MainLayout>
  );
};

export default SalesByVehicle;
